import { OperationVariables, TypedDocumentNode, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, Chip, Stack } from "@mui/material";
import { GridRowParams, GridSortModel, GridValidRowModel } from "@mui/x-data-grid-premium";
import {
  FacilityWasteContainersQuery,
  FacilityWasteContainersQueryVariables,
  TenantWasteContainersQuery,
  TenantWasteContainersQueryVariables,
  WasteContainerListItemFragment,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  FACILITY_WASTE_CONTAINERS_QUERY,
  TENANT_WASTE_CONTAINERS_QUERY,
  WASTE_CONTAINER_FACILITY_NAME_QUERY,
} from "./api";
import {
  containerTypeLabel,
  NO_VALUE_PLACEHOLDER,
  formatAmountCapacity,
  formatContainerDate,
} from "./containerDisplay";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type TenantContainerRow = NonNullable<
  NonNullable<TenantWasteContainersQuery["tenant"]>["wasteContainers"]
>["items"][number];

type ContainerRow = WasteContainerListItemFragment & {
  facility?: { id: string; name?: string | null } | null;
};

/**
 * Column fields match the backend's sortable names (deadlineDate is the
 * persisted min-deadline column the clock's effectiveDeadlineDate mirrors).
 * The containers search is a plain contains-match over label, type, stream,
 * and area — not omnisearch key:value syntax — so the filter popover is off
 * (showFilters={false}) and only the free-text search renders.
 */
function buildColumns<TRow extends ContainerRow>(
  includeFacility: boolean
): OmnisearchGridColDef<TRow>[] {
  const columns: OmnisearchGridColDef<TRow>[] = [
    {
      field: "containerLabel",
      headerName: "Label",
      flex: 0.6,
      minWidth: 120,
      sortable: true,
      filterable: false,
    },
    {
      field: "accumulationArea",
      headerName: "Area",
      flex: 0.6,
      minWidth: 120,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => row.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER,
    },
    {
      field: "wasteStream",
      headerName: "Waste Stream",
      flex: 0.8,
      minWidth: 140,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => row.wasteStream?.name ?? NO_VALUE_PLACEHOLDER,
    },
    {
      field: "amount",
      headerName: "Amount / Capacity",
      flex: 0.6,
      minWidth: 140,
      sortable: true,
      filterable: false,
      headerClassName: "wrapHeader",
      valueGetter: (_, row) => formatAmountCapacity(row),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 130,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => <WasteContainerStatusChip status={row.status} />,
    },
    {
      field: "deadlineDate",
      headerName: "Deadline",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => formatContainerDate(row.clock.effectiveDeadlineDate),
    },
    {
      field: "containerType",
      headerName: "Container Type",
      flex: 0.8,
      minWidth: 150,
      sortable: true,
      filterable: false,
      headerClassName: "wrapHeader",
      valueGetter: (_, row) => containerTypeLabel(row.containerType),
    },
  ];
  if (includeFacility) {
    columns.splice(1, 0, {
      field: "facility",
      headerName: "Facility",
      flex: 0.7,
      minWidth: 130,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => row.facility?.name ?? NO_VALUE_PLACEHOLDER,
    });
  }
  return columns;
}

const INITIAL_SORT: GridSortModel = [{ field: "deadlineDate", sort: "asc" }];

const getTenantItems = (data: TenantWasteContainersQuery) =>
  data.tenant?.wasteContainers?.items ?? [];
const getTenantCount = (data: TenantWasteContainersQuery) =>
  data.tenant?.wasteContainers?.count ?? 0;
const getFacilityItems = (data: FacilityWasteContainersQuery) =>
  data.facility?.wasteContainers?.items ?? [];
const getFacilityCount = (data: FacilityWasteContainersQuery) =>
  data.facility?.wasteContainers?.count ?? 0;

const toApiSortModel = (sort: GridSortModel) => sort.map((s) => ({ field: s.field, sort: s.sort }));

/**
 * The container list rendered for either scope. The tenant and facility grids
 * differ only in their query, data getters, persistence key, empty-row copy,
 * whether the facility column shows, and which id keys the variables — so those
 * are props while every shared grid setting (filters off, initial sort, no row
 * selection) lives here once and can't drift between the two.
 */
function ContainersGrid<
  TData,
  TVariables extends OperationVariables,
  TRow extends ContainerRow & GridValidRowModel,
>({
  tenantId,
  persistenceKey,
  query,
  getItems,
  getCount,
  buildVariables,
  noRowsMessage,
  includeFacility,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  persistenceKey: string;
  query: TypedDocumentNode<TData, TVariables>;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  buildVariables: (
    omnisearch: string,
    pagination: { page: number; pageSize: number },
    sort: GridSortModel
  ) => TVariables;
  noRowsMessage: string;
  includeFacility: boolean;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: React.ReactNode[];
}) {
  const columns = useMemo(() => buildColumns<TRow>(includeFacility), [includeFacility]);
  return (
    <OmnisearchDataGridV2
      persistenceKey={persistenceKey}
      columns={columns}
      showFilters={false}
      query={query}
      getItems={getItems}
      getCount={getCount}
      buildVariables={buildVariables}
      noRowsMessage={noRowsMessage}
      initialSortModel={INITIAL_SORT}
      onRowClick={onRowClick}
      disableRowSelectionOnClick
      commandButtons={commandButtons}
      tenantId={tenantId}
    />
  );
}

function TenantContainersGrid({
  tenantId,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: React.ReactNode[];
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): TenantWasteContainersQueryVariables => ({
      tenantId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [tenantId]
  );
  return (
    <ContainersGrid<
      TenantWasteContainersQuery,
      TenantWasteContainersQueryVariables,
      TenantContainerRow
    >
      tenantId={tenantId}
      persistenceKey="tenant-waste-containers-table"
      query={TENANT_WASTE_CONTAINERS_QUERY}
      getItems={getTenantItems}
      getCount={getTenantCount}
      buildVariables={buildVariables}
      noRowsMessage="No containers are being tracked for this organization."
      includeFacility
      onRowClick={onRowClick}
      commandButtons={commandButtons}
    />
  );
}

function FacilityContainersGrid({
  tenantId,
  facilityId,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  facilityId: string;
  onRowClick: (params: GridRowParams<ContainerRow>) => void;
  commandButtons: React.ReactNode[];
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): FacilityWasteContainersQueryVariables => ({
      facilityId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [facilityId]
  );
  return (
    <ContainersGrid<
      FacilityWasteContainersQuery,
      FacilityWasteContainersQueryVariables,
      WasteContainerListItemFragment
    >
      tenantId={tenantId}
      persistenceKey="facility-waste-containers-table"
      query={FACILITY_WASTE_CONTAINERS_QUERY}
      getItems={getFacilityItems}
      getCount={getFacilityCount}
      buildVariables={buildVariables}
      noRowsMessage="No containers are being tracked at this facility."
      includeFacility={false}
      onRowClick={onRowClick}
      commandButtons={commandButtons}
    />
  );
}

export function WasteContainers() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Containers",
    },
  ]);

  const clearFacilityScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("facilityId");
    setSearchParams(next, { replace: true });
  };

  const handleRowClick = useCallback(
    (params: GridRowParams<ContainerRow>) => {
      navigate(`/o/${tenantId}/waste/containers/${params.row.id}`);
    },
    [navigate, tenantId]
  );

  const commandButtons = useMemo(
    () => [
      <Button
        key="add-container"
        variant="contained"
        size="small"
        startIcon={<Add />}
        onClick={() => setAddDialogOpen(true)}
      >
        Add container
      </Button>,
    ],
    []
  );

  if (!tenantId) {
    return null;
  }

  return (
    <Box>
      {facilityId && (
        <Stack direction="row" sx={{ pt: 2 }}>
          <FacilityScopeChip facilityId={facilityId} onClear={clearFacilityScope} />
        </Stack>
      )}
      <Box sx={{ py: 3 }}>
        {facilityId ? (
          <FacilityContainersGrid
            tenantId={tenantId}
            facilityId={facilityId}
            onRowClick={handleRowClick}
            commandButtons={commandButtons}
          />
        ) : (
          <TenantContainersGrid
            tenantId={tenantId}
            onRowClick={handleRowClick}
            commandButtons={commandButtons}
          />
        )}
      </Box>
      {addDialogOpen && (
        <WasteContainerDialog
          onClose={() => setAddDialogOpen(false)}
          facilityId={facilityId ?? undefined}
          onCreated={(containerId) => navigate(`/o/${tenantId}/waste/containers/${containerId}`)}
        />
      )}
    </Box>
  );
}

/** Deletable chip showing which facility the list is scoped to. */
function FacilityScopeChip({ facilityId, onClear }: { facilityId: string; onClear: () => void }) {
  // cache-only: the sibling facility-scoped grid query already selects
  // facility.name into the normalized cache, so the chip never needs its own
  // round-trip — it shows the generic label until the grid data lands.
  const { data } = useQuery(WASTE_CONTAINER_FACILITY_NAME_QUERY, {
    fetchPolicy: "cache-only",
    variables: { facilityId },
  });
  const facilityName = data?.facility?.name ?? "Facility";
  return (
    <Chip label={`Facility: ${facilityName}`} size="small" variant="outlined" onDelete={onClear} />
  );
}
